import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from '../context/AppContext';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { Card } from '../components/Card';
import { PostType, Tone, GeneratedPost } from '../types';
import { Copy, RefreshCw, Check, Sparkles, Layers, Hash, Image as ImageIcon, Zap, PenSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Box, Typography, Grid, Stack, IconButton, useTheme, CircularProgress, Paper } from '@mui/material';

export default function GeneratePost() {
  const { addPost, usage, refreshData } = useAppContext();
  const [topic, setTopic] = useState('');
  const [postType, setPostType] = useState<PostType>('Caption Post');
  const [tone, setTone] = useState<Tone>('Professional');
  const [audience, setAudience] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPost, setGeneratedPost] = useState<GeneratedPost | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const theme = useTheme();

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (usage.generationsUsed >= usage.planLimit) {
      alert('You have reached your plan limit. Please upgrade to Pro.');
      return;
    }

    setIsGenerating(true);
    setGeneratedPost(null);

    try {
      // Get current session token to authorize the edge function call
      const { data: { session } } = await supabase.auth.getSession();

      const { data, error: funcError } = await supabase.functions.invoke('generate-post', {
        body: { topic, postType, tone, audience },
        headers: session?.access_token ? {
          Authorization: `Bearer ${session.access_token}`,
        } : undefined,
      });

      if (funcError) {
        // Extract actual error message from the edge function response
        let detail = funcError.message;
        try {
          const ctx = (funcError as any).context;
          if (ctx) {
            const body = await ctx.json?.();
            detail = body?.error || JSON.stringify(body) || detail;
          }
        } catch (_) { }
        console.error('Edge Function detail:', detail);
        throw new Error(`Edge Function error: ${detail}`);
      }

      const newPost: GeneratedPost = {
        id: Math.random().toString(36).substr(2, 9), // Temporary ID, will be replaced by DB ID if needed
        topic,
        postType,
        tone,
        audience,
        hook: data.hook,
        caption: data.caption,
        hashtags: data.hashtags,
        imagePrompt: data.imagePrompt,
        carouselSlides: data.carouselSlides,
        createdAt: new Date().toISOString(),
      };

      // Save to Database
      const { data: dbData, error: dbError } = await supabase
        .from('posts')
        .insert({
          user_id: (await supabase.auth.getUser()).data.user?.id,
          topic,
          post_type: postType,
          tone,
          audience,
          hook: data.hook,
          caption: data.caption,
          hashtags: data.hashtags,
          image_prompt: data.imagePrompt,
          carousel_slides: data.carouselSlides,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      if (dbData) {
        newPost.id = dbData.id;
      }

      // Directly increment usage_metrics in DB (reliable, no trigger dependency)
      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (userId) {
        await supabase.rpc('increment_usage', { p_user_id: userId });
      }

      setGeneratedPost(newPost);
      addPost(newPost);

      // Refresh context to get updated data from DB
      await refreshData?.();
    } catch (err: any) {
      console.error('Generation error:', err);
      alert('Error generating post: ' + (err.message || 'Unknown error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <Grid container spacing={4}>
      {/* Input Section */}
      <Grid size={{ xs: 12, lg: 4 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>Generate Post</Typography>
            <Typography variant="body2" color="text.secondary">Fill in the details below to create your next viral post.</Typography>
          </Box>

          <Card sx={{ p: 3 }}>
            <form onSubmit={handleGenerate}>
              <Stack spacing={2.5}>
                <Input
                  label="Post Topic"
                  placeholder="e.g. 5 tips for better sleep"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  required
                />

                <Select
                  label="Post Type"
                  value={postType}
                  onChange={(e) => setPostType(e.target.value as PostType)}
                  options={[
                    { value: 'Caption Post', label: 'Caption Post' },
                    { value: 'Carousel Post', label: 'Carousel Post' },
                    { value: 'Story Idea', label: 'Story Idea' },
                    { value: 'Reel Script', label: 'Reel Script' },
                  ]}
                />

                <Select
                  label="Tone"
                  value={tone}
                  onChange={(e) => setTone(e.target.value as Tone)}
                  options={[
                    { value: 'Professional', label: 'Professional' },
                    { value: 'Funny', label: 'Funny' },
                    { value: 'Motivational', label: 'Motivational' },
                    { value: 'Educational', label: 'Educational' },
                    { value: 'Casual', label: 'Casual' },
                  ]}
                />

                <Input
                  label="Target Audience"
                  placeholder="e.g. Freelance designers"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  required
                />

                <Box pt={1}>
                  <Button type="submit" sx={{ width: '100%' }} isLoading={isGenerating}>
                    <Sparkles size={16} className="mr-2" style={{ marginRight: 8 }} />
                    Generate Content
                  </Button>
                </Box>

                <Typography variant="caption" align="center" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Usage: {usage.generationsUsed} / {usage.planLimit} posts
                </Typography>
              </Stack>
            </form>
          </Card>
        </Stack>
      </Grid>

      {/* Output Section */}
      <Grid size={{ xs: 12, lg: 8 }}>
        <AnimatePresence mode="wait">
          {!generatedPost && !isGenerating ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ height: '100%', minHeight: 400 }}
            >
              <Box sx={{ height: '100%', minHeight: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', border: '2px dashed', borderColor: 'divider', borderRadius: 3, p: 6, bgcolor: 'background.default' }}>
                <Sparkles size={48} style={{ marginBottom: 16, color: theme.palette.divider }} />
                <Typography variant="h6" fontWeight="medium" color="text.secondary" gutterBottom>Your AI-generated content will appear here</Typography>
                <Typography variant="body2">Fill out the form and click generate to start.</Typography>
              </Box>
            </motion.div>
          ) : isGenerating ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ height: '100%', minHeight: 400 }}
            >
              <Box sx={{ height: '100%', minHeight: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'primary.main', p: 6 }}>
                <CircularProgress size={48} sx={{ mb: 3 }} />
                <Typography variant="h6" fontWeight="medium" sx={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>
                  Crafting viral content...
                </Typography>
              </Box>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Stack spacing={3}>
                {/* Hook Card */}
                <OutputCard
                  title="Viral Hook"
                  icon={<Zap size={20} color="#eab308" />}
                  content={generatedPost?.hook || ''}
                  onCopy={() => handleCopy(generatedPost?.hook || '', 'hook')}
                  isCopied={copiedField === 'hook'}
                />

                {/* Caption Card */}
                <OutputCard
                  title="Caption"
                  icon={<PenSquare size={20} color={theme.palette.primary.main} />}
                  content={generatedPost?.caption || ''}
                  onCopy={() => handleCopy(generatedPost?.caption || '', 'caption')}
                  isCopied={copiedField === 'caption'}
                  multiline
                />

                {/* Hashtags Card */}
                <OutputCard
                  title="Hashtags"
                  icon={<Hash size={20} color="#3b82f6" />}
                  content={generatedPost?.hashtags || ''}
                  onCopy={() => handleCopy(generatedPost?.hashtags || '', 'hashtags')}
                  isCopied={copiedField === 'hashtags'}
                />

                {/* Image Prompt Card */}
                <OutputCard
                  title="Image Prompt"
                  icon={<ImageIcon size={20} color="#a855f7" />}
                  content={generatedPost?.imagePrompt || ''}
                  onCopy={() => handleCopy(generatedPost?.imagePrompt || '', 'imagePrompt')}
                  isCopied={copiedField === 'imagePrompt'}
                />

                {/* Carousel Slides (if applicable) */}
                {generatedPost?.carouselSlides && (
                  <Card sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Layers size={20} color="#22c55e" />
                        <Typography variant="h6" fontWeight="semibold">Carousel Slides</Typography>
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(generatedPost.carouselSlides!.join('\n\n'), 'carousel')}
                        color={copiedField === 'carousel' ? 'success' : 'default'}
                      >
                        {copiedField === 'carousel' ? <Check size={16} /> : <Copy size={16} />}
                      </IconButton>
                    </Box>
                    <Stack spacing={1.5}>
                      {generatedPost.carouselSlides.map((slide, index) => (
                        <Box key={index} sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 2, border: 1, borderColor: 'divider', typography: 'body2' }}>
                          {slide}
                        </Box>
                      ))}
                    </Stack>
                  </Card>
                )}
              </Stack>
            </motion.div>
          )}
        </AnimatePresence>
      </Grid>
    </Grid>
  );
}

function OutputCard({
  title,
  icon,
  content,
  onCopy,
  isCopied,
  multiline = false
}: {
  title: string;
  icon: React.ReactNode;
  content: string;
  onCopy: () => void;
  isCopied: boolean;
  multiline?: boolean;
}) {
  return (
    <Card sx={{ p: 3, position: 'relative', '&:hover .copy-btn': { opacity: 1 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography variant="h6" fontWeight="semibold">{title}</Typography>
        </Stack>
        <IconButton
          size="small"
          onClick={onCopy}
          className={isCopied ? '' : 'copy-btn'}
          sx={{ opacity: isCopied ? 1 : { xs: 1, sm: 0 }, transition: 'opacity 0.2s' }}
          color={isCopied ? 'success' : 'default'}
        >
          {isCopied ? <Check size={16} /> : <Copy size={16} />}
        </IconButton>
      </Box>
      <Typography
        variant="body2"
        color="text.primary"
        sx={{ whiteSpace: multiline ? 'pre-wrap' : 'normal', lineHeight: 1.6 }}
      >
        {content}
      </Typography>
    </Card>
  );
}
